import React, {
  useState,
  useRef,
  useContext
}                               from 'react';
import { useTranslation}        from 'react-i18next';
import { toast }                from 'react-toastify';
import BusinessIcon             from '@material-ui/icons/Business';
import CorporationContext       from './context';
import List                     from '~/containers/List';
import Request                  from '~/providers/Request';
import ModalDelete              from '~/components/ModalDelete';
import {
  showCorporation,
  editCorporation,
  createCorporation
}                               from '~/config/routes';
import moment                   from 'moment';
import { AbilityContext } from '~/contexts/Can';

function CorporationList() {
  const { t } = useTranslation();
  const [modalInfo, setModalInfo] = useState({ show: false });
  const { delRecord } = useContext(CorporationContext);
  const ability = useContext(AbilityContext);
  const listEl = useRef(null);

  const trAttr = (attr) => t(`corporation.attributes.${attr}`);

  function handleDeletionModal(id, title){
    setModalInfo({ show: true, id, title });
  }

  async function handleDelete(){
    try {
      await Request.del(`/admin/corporation/${modalInfo.id}`);
      delRecord(modalInfo.id);
      listEl.current.onQueryChange();
      toast.success(t('delete-toast-success'));
    } catch(e) {
      toast.error(t('delete-toast-fail'));
    } finally {
      setModalInfo({ show: false });
    }
  }

  const actions = [
    'search',
    'filter',
  ];

  if(ability.can('new', 'Corporations')) {
    actions.push('new');
  }
  if(ability.can('edit', 'Corporations')) {
    actions.push('edit');
  }
  if(ability.can('delete', 'Corporations')) {
    actions.push('del');
  }
  if(ability.can('show', 'Corporations')) {
    actions.push('show');
  }

  return (
    <div>
      <List
        deleteAction={handleDeletionModal}
        routeToNew={createCorporation}
        routeToEdit={editCorporation}
        routeToShow={showCorporation}
        listEl={listEl}
        path={'/admin/corporation/'}
        actions={actions}
        paperProps={{
          Icon: BusinessIcon,
          iconColor: 'blue',
          title: t('corporation.defPlural'),
        }}
        columns={[
          { title: trAttr('id'), field: 'id', type: 'numeric', precision: 0 },
          { title: trAttr('name'), field: 'name', type: 'string' },
          { title: trAttr('email'), field: 'email', type: 'string', hidden: true },
          { title: trAttr('phone'), field: 'phone', type: 'string', hidden: true },
          { title: trAttr('cnpj'), field: 'cnpj', type: 'string' },
          { title: trAttr('corporation'), field: 'corporation', type: 'string' },
          { title: trAttr('city'), field: 'city', type: 'string' },
          { title: trAttr('street'), field: 'street', type: 'string', hidden: true },
          { title: trAttr('district'), field: 'district', type: 'string', hidden: true },
          { title: trAttr('number'), field: 'number', type: 'numeric', precision: 0, hidden: true },
          { title: trAttr('postal_code'), field: 'postal_code', type: 'string', hidden: true },
          { title: trAttr('contact_name'), field: 'contact_name', type: 'string', hidden: true },
          { title: trAttr('contact_phone'), field: 'contact_phone', type: 'string', hidden: true },
          { title: trAttr('contract_expires_at'), field: 'contract_expires_at', type: 'datetime' },
          { title: trAttr('is_doctor'), field: 'is_doctor', type: 'boolean' },
          { title: trAttr('is_emergency'), field: 'is_emergency', type: 'boolean' },
          { title: trAttr('is_driving'), field: 'is_driving', type: 'boolean' },
          { title: trAttr('users_limit'), field: 'users_limit', type: 'numeric' },
          { title: trAttr('is_plan'), field: 'is_plan', type: 'boolean' },
          { title: trAttr('disabled_at'), field: 'disabled_at', type: 'datetime' },
          { title: trAttr('frozen_at'), field: 'frozen_at', type: 'datetime' },
          { title: trAttr('corporation_type'), field: 'corporation_type', type: 'string', hidden: true },
          { title: trAttr('wallet_id'), field: 'wallet_id', type: 'string', hidden: true },
          { title: trAttr('landing_url'), field: 'landing_url', type: 'string', hidden: true },
          { title: trAttr('accession_percentage'), field: 'accession_percentage', type: 'numeric', precision: 2, hidden: true },
          { title: trAttr('signature_percentage'), field: 'signature_percentage', type: 'numeric', precision: 2, hidden: true },
          { title: trAttr('revenue'), field: 'revenue', type: 'numeric', precision: 2, hidden: true },
        ]}
        context={CorporationContext}
        recordMap={
          record => ({
            ...record,
            city: record.city ? record.city.name : '',
            corporation: record.corporation ? record.corporation.name : '',
            contract_expires_at: record.contract_expires_at ? moment(record.contract_expires_at).format('DD/MM/YYYY') : null,
            created_at: record.created_at ? moment(record.created_at).format('DD/MM/YYYY HH:mm') : null,
            updated_at: record.updated_at ? moment(record.updated_at).format('DD/MM/YYYY HH:mm') : null,
            disabled_at: record.disabled_at ? moment(record.disabled_at).format('DD/MM/YYYY HH:mm') : null,
            frozen_at: record.frozen_at ? moment(record.frozen_at).format('DD/MM/YYYY HH:mm') : null,
            corporation_type: record.config_corporation?.corporation_type || '',
            wallet_id: record.config_corporation?.wallet_id || '',
            landing_url: record.config_corporation?.landing_url || '',
            accession_percentage: record.config_corporation?.accession_percentage || 0,
            signature_percentage: record.config_corporation?.signature_percentage || 0,
            revenue: record.config_corporation?.revenue || 0,
          })
        }
      />

      <ModalDelete
        id={modalInfo.id}
        name={modalInfo.title}
        modal={modalInfo.show}
        deleteAction={handleDelete}
        onClose={() => setModalInfo({ show: false })}
      />
    </div>
  );
}

export default React.memo(CorporationList);
